import React from "react";
import { useQuery, useAction } from "wasp/client/operations";
import {
  getRequestOfferById,
  deleteRequestOffer,
} from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualViewPageLayout,
  SidebarSection,
  ActionButtons,
  DetailField,
  ActionButton,
  ActionButtonDanger,
} from "../../components/layout";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  Edit,
  Trash2,
  DollarSign,
  Tag,
  Phone,
  Calendar,
  User,
  Mail,
} from "lucide-react";
import { SubscribeButton } from "../content/components/SubscribeButton";

export default function ViewRequestOfferPage() {
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const {
    data: item,
    isLoading,
    error,
  } = useQuery(getRequestOfferById, { itemId: itemId || "" });
  const deleteRequestOfferFn = useAction(deleteRequestOffer);

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/request-offer`);
    } else {
      navigate("/request-offer");
    }
  };

  const handleEdit = () => {
    if (selectedLocation?.locationCode) {
      navigate(
        `/locations/${selectedLocation.locationCode}/request-offer/${itemId}/edit`,
      );
    } else {
      navigate(`/request-offer/${itemId}/edit`);
    }
  };

  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this item?")) {
      try {
        await deleteRequestOfferFn({ itemId: itemId || "" });
        // Navigate back to the request-offer page
        if (selectedLocation?.locationCode) {
          navigate(`/locations/${selectedLocation.locationCode}/request-offer`);
        } else {
          navigate("/request-offer");
        }
      } catch (error) {
        console.error("Error deleting item:", error);
        alert("Error deleting item. Please try again.");
      }
    }
  };

  // Authentication check
  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view items.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!selectedLocation) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              No Location Selected
            </h2>
            <p className="text-gray-600">
              Please select a location to view items.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading item...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !item) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Item Not Found
            </h2>
            <p className="text-gray-600">
              The item you're looking for doesn't exist.
            </p>
            <button
              onClick={handleGoBack}
              className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go Back
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getCategoryColor = (category: string) => {
    return category === "REQUEST"
      ? "bg-green-100 text-green-800"
      : "bg-blue-100 text-blue-800";
  };

  const getConditionColor = (condition: string) => {
    const colors = {
      NEW: "bg-green-100 text-green-800",
      LIKE_NEW: "bg-blue-100 text-blue-800",
      GOOD: "bg-yellow-100 text-yellow-800",
      FAIR: "bg-orange-100 text-orange-800",
      POOR: "bg-red-100 text-red-800",
    };
    return (
      colors[condition as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };

  const isAdmin = user?.isAdmin;

  const currentPageUrl =
    typeof window !== "undefined" ? window.location.href : "";

  const breadcrumbItems = [
    {
      label: "Community",
      dropdownItems: [
        {
          label: "Buy and Sell",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/buy-sell`
            : "/buy-sell",
        },
        {
          label: "Request & Offer",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/request-offer`
            : "/request-offer",
        },
        {
          label: "Food Court",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/food-court`
            : "/food-court",
        },
        {
          label: "Lost and Found",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/lost-found`
            : "/lost-found",
        },
        {
          label: "Retail",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/retail`
            : "/retail",
        },
        {
          label: "Services",
          href: selectedLocation?.locationCode
            ? `/locations/${selectedLocation.locationCode}/services`
            : "/services",
        },
      ],
    },
    {
      label: "Request & Offer",
      isClickable: true,
      href: selectedLocation?.locationCode
        ? `/locations/${selectedLocation.locationCode}/request-offer`
        : "/request-offer",
    },
  ];

  const sidebarContent = (
    <>
      {isAdmin && (
        <SidebarSection isFirst>
          <ActionButtons>
            <ActionButton onClick={handleEdit} icon={Edit}>
              Edit Item
            </ActionButton>
            <ActionButtonDanger onClick={handleDelete} icon={Trash2}>
              Delete Item
            </ActionButtonDanger>
          </ActionButtons>
        </SidebarSection>
      )}

      <SidebarSection title="Details">
        <div className="space-y-4">
          <DetailField
            label="Category"
            value={(item as any).metadata?.category || ""}
          />
          {(item as any).metadata?.condition && (
            <DetailField
              label="Condition"
              value={(item as any).metadata.condition?.replace("_", " ")}
            />
          )}
          <DetailField
            label="Posted by"
            value={
              (item as any).author?.name || (item as any).author?.email || ""
            }
          />
          <DetailField
            label="Posted on"
            value={new Date((item as any).createdAt).toLocaleDateString()}
          />
        </div>
      </SidebarSection>
    </>
  );

  return (
    <IndividualViewPageLayout
      breadcrumbItems={breadcrumbItems}
      currentPageUrl={currentPageUrl}
      sidebarContent={sidebarContent}
    >
      <div className="flex items-center gap-3 mb-4">
        <span
          className={`px-3 py-1 text-sm font-medium rounded ${getCategoryColor(
            (item as any).metadata?.category,
          )}`}
        >
          {(item as any).metadata?.category}
        </span>
        {(item as any).metadata?.condition && (
          <span
            className={`px-3 py-1 text-sm font-medium rounded ${getConditionColor(
              (item as any).metadata.condition,
            )}`}
          >
            {(item as any).metadata.condition?.replace("_", " ")}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold text-gray-900">
          {(item as any).title}
        </h1>
        <SubscribeButton contentId={item.id} />
      </div>

      {(item as any).metadata?.price && (
        <div className="text-3xl font-bold text-green-600 mb-6">
          ${(item as any).metadata.price}
        </div>
      )}

      {/* Description */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Description
        </h2>
        <div className="prose max-w-none">
          <p className="text-gray-700 whitespace-pre-wrap">
            {(item as any).description}
          </p>
        </div>
      </div>

      {/* Item Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Item Information
          </h3>
          <dl className="space-y-3">
            <div className="flex items-center gap-3">
              <Tag className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Category:</dt>
              <dd className="text-sm text-gray-900">
                {(item as any).metadata?.category}
              </dd>
            </div>

            {(item as any).metadata?.condition && (
              <div className="flex items-center gap-3">
                <dt className="text-sm font-medium text-gray-500">
                  Condition:
                </dt>
                <dd className="text-sm text-gray-900">
                  {(item as any).metadata.condition?.replace("_", " ")}
                </dd>
              </div>
            )}

            {(item as any).metadata?.price && (
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Price:</dt>
                <dd className="text-sm text-gray-900">
                  ${(item as any).metadata.price}
                </dd>
              </div>
            )}
          </dl>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Contact Information
          </h3>
          <dl className="space-y-3">
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Posted by:</dt>
              <dd className="text-sm text-gray-900">
                {(item as any).author?.name || (item as any).author?.email}
              </dd>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <dt className="text-sm font-medium text-gray-500">Posted on:</dt>
              <dd className="text-sm text-gray-900">
                {new Date((item as any).createdAt).toLocaleDateString()}
              </dd>
            </div>

            {(item as any).metadata?.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Email:</dt>
                <dd className="text-sm text-gray-900">
                  <a href={`mailto:${(item as any).metadata.email}`} className="text-blue-600 hover:underline">
                    {(item as any).metadata.email}
                  </a>
                </dd>
              </div>
            )}

            {(item as any).metadata?.phoneNumber && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Phone:</dt>
                <dd className="text-sm text-gray-900">
                  <a href={`tel:${(item as any).metadata.phoneNumber}`} className="text-blue-600 hover:underline">
                    {(item as any).metadata.phoneNumber}
                  </a>
                </dd>
              </div>
            )}

            {(item as any).metadata?.contactInfo && !(item as any).metadata?.email && !(item as any).metadata?.phoneNumber && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-gray-400" />
                <dt className="text-sm font-medium text-gray-500">Contact:</dt>
                <dd className="text-sm text-gray-900">
                  {(item as any).metadata.contactInfo}
                </dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </IndividualViewPageLayout>
  );
}
